import { Flex, Text } from '@chakra-ui/react'
import {
  ArrowUpDown,
  Merge,
  MicOff,
  Pause,
  Phone,
  PhoneOff,
  PhoneOutgoing,
  Play,
  Plus,
  Split,
} from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useModalState } from '~/atoms/ModalState'
import { CallMode, useSettingsState } from '~/atoms/SettingsState'
import { useSipInstance } from '~/atoms/SipInstanceState'
import { easeInOutTransition } from '~/components/CommonStyles'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { bottomLeftToaster } from '~/components/ui/toaster'
import { useCallDuration } from '~/hooks/useCallDuration'
import { useCallPopEnabled } from '~/hooks/useCallPopEnabled'
import useCloudwatchLogger from '~/hooks/useCloudwatchLogger'
import { useNetworkConnectivity } from '~/hooks/useNetworkConnectivity'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'
import { getDisplayCallerInfo } from '~/utils/phoneHelpers'

import {
  isPreConnectedStatus,
  PhoneCall,
  PhoneCallStatus,
} from '@mangovoice/types'

import {
  CallBlockVariants,
  useCallBlockStyle,
} from './componentStyles/CallBlock.style'
import { FEEDBACK_TOAST_ID, showFeedbackToast } from './FeedbackToast'

const RingbackToneUri = '/ringbacktone.wav'
// The shared live region holds one message, so give a hang-up announcement
// made at the same moment time to be spoken before the feedback prompt.
const FEEDBACK_ANNOUNCEMENT_DELAY_MS = 1000

interface CollapsableWrapperProps {
  $collapsed?: boolean
}
export const CollapsableWrapper = styled.div<CollapsableWrapperProps>`
  display: ${({ $collapsed }) => ($collapsed ? 'none' : 'default')};
  ${easeInOutTransition}
`

const getCallBlockVariant = (isRinging: boolean, isOnHold: boolean) => {
  if (isRinging) {
    return CallBlockVariants.Ringing
  }
  if (isOnHold) {
    return CallBlockVariants.OnHold
  }
  return CallBlockVariants.Connected
}

interface CallBlockProps {
  calls: PhoneCall[]
  callUuid: string
  callStatus: PhoneCallStatus
  isRinging: boolean
  isActiveCall: boolean
  hasActiveCall: boolean
  startTime: Date | null
  number: string
  onEndCall: (callUuid: string) => void
  onMute: (callUuid: string) => void
  isMuted: boolean
  onHold: (callUuid: string) => Promise<void>
  isOnHold: boolean
  isCollapsed?: boolean
  isOnline: boolean
  onSwitchCall: (callUuid: string) => Promise<void>
  onTransferCall: (callUuid: string) => void
  onNavigateToWebPhone: () => void
  onAnswerCall: (callUuid: string) => void
  callSource?: 'softphone' | 'deskphone'
  pendingInvitationUuid?: string | null
}
const CallBlock = ({
  calls,
  callUuid,
  callStatus,
  isRinging,
  isActiveCall,
  hasActiveCall,
  startTime,
  number,
  onEndCall,
  onMute,
  isMuted,
  onHold,
  isOnHold,
  isCollapsed,
  isOnline,
  onSwitchCall,
  onTransferCall,
  onNavigateToWebPhone,
  onAnswerCall,
  callSource,
  pendingInvitationUuid,
}: CallBlockProps) => {
  const { t } = useTranslation()
  const callDuration = useCallDuration(startTime)
  const effectiveCallPopEnabled = useCallPopEnabled()
  const isDeskPhoneCall = callSource === 'deskphone'
  const isPendingInvitation = callUuid === pendingInvitationUuid

  const shouldShowAnswerButton =
    isRinging &&
    !effectiveCallPopEnabled &&
    !isPendingInvitation &&
    !isDeskPhoneCall

  const handleEndCall = () => {
    onEndCall(callUuid)
  }

  const handleMute = () => {
    onMute(callUuid)
  }

  const handleHold = async () => {
    if (effectiveCallPopEnabled) {
      // Use the same IPC mechanism as popup to ensure CallQueue sync
      window.electron?.send(
        isOnHold ? 'popup-resume-call' : 'popup-hold-call',
        callUuid,
      )
    }
    await onHold(callUuid)
  }

  const handleSwitchCall = async () => {
    if (effectiveCallPopEnabled) {
      // Use IPC mechanism to ensure CallQueue sync
      window.electron?.send('popup-switch-call', callUuid)
      return
    }
    await onSwitchCall(callUuid)
  }

  const handleTransferCall = () => {
    onTransferCall(callUuid)
  }

  const handleAnswerCall = () => {
    onAnswerCall(callUuid)
  }

  const handleBadgeClick = () => {
    if (isCollapsed) {
      if (isActiveCall) {
        onNavigateToWebPhone()
      } else {
        void onSwitchCall(callUuid)
      }
    }
  }

  const getCallStatusText = () => {
    if (isDeskPhoneCall) {
      if (isCollapsed) return t('callStatus.shortDesk')
      return t('callStatus.onDeskPhone')
    }
    if (!isOnline) {
      return t('callStatus.reconnecting')
    }
    if (callStatus === PhoneCallStatus.CONNECTING) {
      if (isCollapsed) return t('callStatus.shortDial')
      return t('callStatus.dialing')
    }
    if (isRinging) {
      if (isCollapsed) return t('callStatus.shortRing')
      return t('callStatus.ringing')
    }
    if (isOnHold) {
      if (isCollapsed) return t('callStatus.shortHold')
      return t('callStatus.onHold')
    }
    if (!startTime) {
      if (isCollapsed) return t('callStatus.shortConn')
      return t('callStatus.connecting')
    }
    if (isCollapsed) {
      return `${callDuration}`
    }
    return t('callStatus.connectedWithDuration', { duration: callDuration })
  }

  const variant = getCallBlockVariant(isRinging, isOnHold)
  const styles = useCallBlockStyle({ variant })

  const badgeCursor = isCollapsed ? 'pointer' : 'default'

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    gap: styles.container?.gap,
  }

  const controlBarProps = {
    gap: styles.controlBar?.gap,
  }

  // Hide non-active, non-held calls when there's an active connected call
  const hasActiveConnectedCall = calls.some(
    (call) =>
      call.callUuid !== callUuid &&
      call.status === PhoneCallStatus.CONNECTED &&
      !call.isOnHold,
  )

  if (!isActiveCall && !isOnHold && !isRinging && hasActiveConnectedCall) {
    return null
  }

  return (
    <Flex
      css={styles.container}
      paddingX={isCollapsed ? '0.25rem' : undefined}
      {...containerProps}
    >
      {isActiveCall && callStatus === PhoneCallStatus.RINGING && (
        <audio id="ringback" src={RingbackToneUri} autoPlay loop />
      )}
      <Text
        as={'span'}
        css={styles.statusBadge}
        onClick={handleBadgeClick}
        cursor={badgeCursor}
      >
        {getCallStatusText()}
      </Text>

      <CollapsableWrapper $collapsed={isCollapsed}>
        <Text as={'span'} css={styles.number}>
          {number}
        </Text>
        {!isDeskPhoneCall && (
          <Flex css={styles.controlBar} {...controlBarProps}>
            <QuickActionButton
              variant={QuickActionButtonVariants.Destructive_100}
              size={Size.sm}
              actionIcon={PhoneOff}
              tooltipText={t('webPhone.tooltip.endCall')}
              onClick={handleEndCall}
              iconProps={{ boxSize: '20px' }}
            />
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_150}
              size={Size.sm}
              actionIcon={MicOff}
              tooltipText={t('webPhone.tooltip.muteCall')}
              isActive={isMuted}
              onClick={handleMute}
              iconProps={{ boxSize: '24px' }}
            />

            {(isActiveCall || !hasActiveCall) && !isRinging && (
              <QuickActionButton
                variant={QuickActionButtonVariants.Default_150}
                size={Size.sm}
                actionIcon={isOnHold ? Play : Pause}
                tooltipText={
                  isOnHold
                    ? t('webPhone.tooltip.resumeCall')
                    : t('webPhone.tooltip.holdCall')
                }
                isActive={isOnHold}
                onClick={handleHold}
                iconProps={isOnHold ? { boxSize: '16px' } : { boxSize: '24px' }}
              />
            )}

            {!isActiveCall && hasActiveCall && calls.length > 1 && (
              <>
                {shouldShowAnswerButton && (
                  <QuickActionButton
                    variant={QuickActionButtonVariants.Default_150}
                    size={Size.sm}
                    actionIcon={Phone}
                    tooltipText={t('webPhone.tooltip.answerCall')}
                    onClick={handleAnswerCall}
                    iconProps={{ boxSize: '20px' }}
                  />
                )}
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_150}
                  size={Size.sm}
                  actionIcon={ArrowUpDown}
                  tooltipText={t('webPhone.tooltip.switchToCall')}
                  onClick={handleSwitchCall}
                  iconProps={{ boxSize: '28px' }}
                />
                <QuickActionButton
                  variant={QuickActionButtonVariants.Default_150}
                  size={Size.sm}
                  actionIcon={PhoneOutgoing}
                  tooltipText={t('webPhone.tooltip.warmTransfer')}
                  onClick={handleTransferCall}
                  iconProps={{ boxSize: '24px' }}
                />
              </>
            )}
          </Flex>
        )}
      </CollapsableWrapper>
    </Flex>
  )
}

interface ConferenceBlockProps {
  startTime?: Date
  numberOfParticipants: number
  onEndConference: () => void
  onMuteConference: () => void
  isMuted: boolean
  onHoldConference: () => Promise<void>
  isOnHold: boolean
  isCollapsed?: boolean
  onNavigateToWebPhone: () => void
}
const ConferenceBlock = ({
  startTime,
  numberOfParticipants,
  onEndConference,
  onMuteConference,
  isMuted,
  onHoldConference,
  isOnHold,
  isCollapsed,
  onNavigateToWebPhone,
}: ConferenceBlockProps) => {
  const { t } = useTranslation()
  const callDuration = useCallDuration(startTime)

  const handleEndConference = () => {
    onEndConference()
  }

  const handleMuteConference = () => {
    onMuteConference()
  }

  const handleHoldConference = async () => {
    await onHoldConference()
  }

  const handleBadgeClick = () => {
    if (isCollapsed) {
      onNavigateToWebPhone()
    }
  }

  const getCallStatusText = () => {
    if (isOnHold) {
      if (isCollapsed) return t('callStatus.shortHold')
      return t('callStatus.onHold')
    }
    if (isCollapsed) {
      return `${callDuration}`
    }
    return t('callStatus.connectedWithDuration', { duration: callDuration })
  }

  const variant = getCallBlockVariant(false, isOnHold)
  const styles = useCallBlockStyle({ variant })
  const badgeCursor = isCollapsed ? 'pointer' : 'default'

  // Extract layout properties to apply as native props (Chakra v3 requirement)
  const containerProps = {
    flexDirection: styles.container?.flexDirection,
    gap: styles.container?.gap,
  }

  const controlBarProps = {
    gap: styles.controlBar?.gap,
  }

  return (
    <Flex
      css={styles.container}
      paddingX={isCollapsed ? '0.25rem' : undefined}
      {...containerProps}
    >
      <Text
        as={'span'}
        css={styles.statusBadge}
        onClick={handleBadgeClick}
        cursor={badgeCursor}
      >
        {getCallStatusText()}
      </Text>

      <CollapsableWrapper $collapsed={isCollapsed}>
        <Text css={styles.number}>{numberOfParticipants} Participants</Text>
        <Flex css={styles.controlBar} {...controlBarProps}>
          <QuickActionButton
            variant={QuickActionButtonVariants.Destructive_100}
            size={Size.sm}
            actionIcon={PhoneOff}
            tooltipText={t('webPhone.tooltip.endCall')}
            onClick={handleEndConference}
            iconProps={{ boxSize: '20px' }}
          />
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_150}
            size={Size.sm}
            actionIcon={MicOff}
            tooltipText={t('webPhone.tooltip.muteCall')}
            isActive={isMuted}
            onClick={handleMuteConference}
            iconProps={{ boxSize: '24px' }}
          />
          <QuickActionButton
            variant={QuickActionButtonVariants.Default_150}
            size={Size.sm}
            actionIcon={Pause}
            tooltipText={t('webPhone.tooltip.holdCall')}
            isActive={isOnHold}
            onClick={handleHoldConference}
            iconProps={{ boxSize: '24px' }}
          />
        </Flex>
      </CollapsableWrapper>
    </Flex>
  )
}

export const ActiveCallsBlock = ({
  isCollapsed,
}: {
  isCollapsed?: boolean
}) => {
  const { t } = useTranslation()
  const { logger } = useCloudwatchLogger('ActiveCallsBlock')
  const announce = useAnnounce()
  const pendingInvitationUuidRef = React.useRef<string | null>(null)
  const feedbackAnnouncementTimerRef = React.useRef<ReturnType<
    typeof setTimeout
  > | null>(null)
  React.useEffect(
    () => () => {
      if (feedbackAnnouncementTimerRef.current) {
        clearTimeout(feedbackAnnouncementTimerRef.current)
      }
    },
    [],
  )
  const {
    sipInstance: [getSipInstance],
  } = useSipInstance()
  const sipClient = getSipInstance()
  const sipConnected = sipClient?.isConnected() ?? false

  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        list: { callList },
        conference: {
          conferencedCalls,
          isMuted: conferenceIsMuted,
          isOnHold: conferenceIsOnHold,
          startTime: conferenceStartTime,
        },
        invite: { pendingInvitationUuid },
        isAtMaxCallLimit,
      },
    },
    actions: {
      endCall,
      muteCall,
      holdCall,
      switchCall,
      attendedTransferCall,
      mergeCalls,
      splitCalls,
      endConferenceCall,
      toggleMuteConference,
      toggleHoldConference,
      answerCall,
    },
  } = usePhoneEndpoint({
    onFinalCallEnd: () => {
      if (Math.random() < 0.1) {
        logger?.info('call_fb_displayed')
        showFeedbackToast(() => {
          setFeedbackModalState({ open: true })
        })
        const prompt = t(
          'webPhone.feedback.toast.header',
          'Rate your call audio',
        )
        if (feedbackAnnouncementTimerRef.current) {
          clearTimeout(feedbackAnnouncementTimerRef.current)
        }
        feedbackAnnouncementTimerRef.current = setTimeout(() => {
          feedbackAnnouncementTimerRef.current = null
          // Don't talk over a new incoming call, or prompt for a toast that
          // has already gone.
          if (pendingInvitationUuidRef.current) return
          if (!bottomLeftToaster.isVisible(FEEDBACK_TOAST_ID)) return
          announce(prompt)
        }, FEEDBACK_ANNOUNCEMENT_DELAY_MS)
      }
    },
  })
  pendingInvitationUuidRef.current = pendingInvitationUuid

  const navigate = useNavigate()

  const {
    feedbackModal: [, setFeedbackModalState],
    callModal: [, setCallModalState],
  } = useModalState()
  const isOnline = useNetworkConnectivity({
    playConnectionChangeSound: !!activeCall,
    reportAnalytics: true,
  })

  const {
    settings: {
      webPhone: { doNotDisturbTimestamp },
    },
  } = useSettingsState()
  const doNotDisturb: boolean = doNotDisturbTimestamp !== undefined
  const effectiveCallPopEnabled = useCallPopEnabled()

  // CallQueue sync is now handled by useHybridCallState in HybridCallManager

  // Don't show Active Call Block if SIP is not connected
  if (!sipConnected) {
    return null
  }

  const handleNewCallClick = () => {
    setCallModalState({ open: true, overrideCallMode: CallMode.WEBPHONE })
  }

  const handleTransferCall = (callUuid: string) => {
    if (!activeCall) return
    void attendedTransferCall(callUuid, activeCall.callUuid).catch(
      () => undefined,
    )
  }

  const handleMergeCalls = () => {
    const callUuids = Array.from(callList.keys())
    mergeCalls(callUuids)
  }
  const handleSplitCalls = () => {
    splitCalls()
  }
  const handleMuteConference = () => {
    toggleMuteConference()
  }
  const handleHoldConference = async () => {
    await toggleHoldConference()
  }
  const handleEndConference = () => {
    endConferenceCall()
  }

  if (callList.size === 0) {
    return <div />
  }

  const getCallBlocks = () => {
    if (conferencedCalls) {
      return [
        <ConferenceBlock
          key="conference"
          startTime={conferenceStartTime}
          numberOfParticipants={conferencedCalls.length}
          onEndConference={handleEndConference}
          onMuteConference={handleMuteConference}
          isMuted={conferenceIsMuted}
          onHoldConference={handleHoldConference}
          isOnHold={conferenceIsOnHold}
          isCollapsed={isCollapsed}
          onNavigateToWebPhone={() => navigate(ApplicationRoutes.WEB_PHONE)}
        />,
      ]
    }

    // Filter out desk phone calls - they're managed by the physical phone, not the web UI
    // Ignore all deskphone calls when popup is disabled
    let calls = Array.from(callList.values()).filter((call) => {
      // Filter out deskphone calls when popup is disabled
      if (call.callSource === 'deskphone') {
        return effectiveCallPopEnabled
      }
      return true
    })

    // Filter out ALL ringing calls when DND is enabled (they should be rejected, not shown)
    // This is a safety net in case there's a brief race condition before rejection completes
    // or if WebSocket events try to re-add rejected calls
    if (doNotDisturb) {
      calls = calls.filter((call) => !isPreConnectedStatus(call.status))
    }

    return calls.map((call: PhoneCall) => {
      const displayInfo = getDisplayCallerInfo(call, true) // true = prefer SIP
      const formattedNumber = call.isPageOrIntercom
        ? t('popup.callItem.pageIntercom')
        : formatPhoneNumber(displayInfo.number)

      return (
        <CallBlock
          calls={calls}
          key={call.callUuid}
          isOnline={isOnline}
          callUuid={call.callUuid}
          callStatus={call.status}
          isRinging={isPreConnectedStatus(call.status)}
          isActiveCall={activeCall?.callUuid === call.callUuid}
          hasActiveCall={activeCall !== undefined}
          startTime={call.startTime}
          number={formattedNumber}
          onEndCall={endCall}
          onMute={muteCall}
          isMuted={call.isMuted}
          onHold={holdCall}
          isOnHold={call.isOnHold}
          isCollapsed={isCollapsed}
          onSwitchCall={switchCall}
          onTransferCall={handleTransferCall}
          onNavigateToWebPhone={() => navigate(ApplicationRoutes.WEB_PHONE)}
          onAnswerCall={answerCall}
          callSource={call.callSource}
          pendingInvitationUuid={pendingInvitationUuid}
        />
      )
    })
  }

  // HACK: Something breaks when using this specific style config, couldn't figure it out
  // const styles = useActiveCallsBlockStyle()
  return (
    <Flex flexDirection="column">
      <CollapsableWrapper $collapsed={isCollapsed}>
        <Flex
          justify="space-between"
          align="center"
          borderBottom="1px solid"
          borderColor="border.default"
          py="4px"
          px="12px"
          // css={styles.header}
        >
          <Text
            // css={styles.text}
            lineClamp={1}
            textStyle="Body2"
          >
            {t('webPhoneExtras.activeCall', 'Active Call')}
          </Text>
          {!isAtMaxCallLimit && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_150}
              size={Size.sm}
              actionIcon={Plus}
              tooltipText={t('webPhone.tooltip.newCall')}
              onClick={handleNewCallClick}
            />
          )}
          {isAtMaxCallLimit && !conferencedCalls && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_150}
              size={Size.sm}
              actionIcon={Merge}
              tooltipText={t('webPhone.tooltip.mergeCalls')}
              onClick={handleMergeCalls}
            />
          )}
          {isAtMaxCallLimit && conferencedCalls && (
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={Split}
              tooltipText={t('webPhone.tooltip.endConference')}
              onClick={handleSplitCalls}
            />
          )}
        </Flex>
      </CollapsableWrapper>
      {getCallBlocks()}
    </Flex>
  )
}
